import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

function formatPhone(value) {
  const digits = (value || '').replace(/\D/g, '');
  const parts = [];
  for (let i = 0; i < digits.length && i < 10; i += 2) {
    parts.push(digits.substring(i, i + 2));
  }
  return parts.join(' ');
}

// ─── Styles partagés ──────────────────────────────────────────────────────────
const S = {
  tabs: { display: 'flex', gap: '0', marginBottom: '0', borderBottom: '2px solid #ecf0f1' },
  tab: (active) => ({
    padding: '0.75rem 1.5rem', cursor: 'pointer', fontWeight: 600,
    fontSize: '0.95rem', border: 'none', background: 'none',
    borderBottom: active ? '3px solid #3498db' : '3px solid transparent',
    color: active ? '#3498db' : '#7f8c8d',
    marginBottom: '-2px', transition: 'all 0.2s',
  }),
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  },
  dialog: {
    background: '#fff', borderRadius: '8px', width: '620px', maxWidth: '95vw',
    maxHeight: '90vh', display: 'flex', flexDirection: 'column',
    boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
  },
  dialogHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '1.25rem 1.5rem', background: '#2c3e50', borderRadius: '8px 8px 0 0',
  },
  dialogTitle: { color: '#fff', fontSize: '1.1rem', fontWeight: 600, margin: 0 },
  closeBtn: { background: 'none', border: 'none', color: '#fff', fontSize: '1.5rem', cursor: 'pointer' },
  dialogBody: { padding: '1.5rem', overflowY: 'auto', flex: 1 },
  dialogFooter: {
    padding: '1rem 1.5rem', borderTop: '1px solid #ecf0f1',
    display: 'flex', justifyContent: 'flex-end', gap: '0.75rem',
    background: '#f8f9fa', borderRadius: '0 0 8px 8px',
  },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' },
  grid3: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' },
  sectionTitle: {
    fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: '0.08em', color: '#7f8c8d',
    margin: '1.25rem 0 0.75rem', paddingBottom: '0.35rem',
    borderBottom: '1px solid #ecf0f1',
  },
  label: { display: 'block', marginBottom: '0.3rem', fontSize: '0.875rem', fontWeight: 500, color: '#2c3e50' },
  required: { color: '#e74c3c', marginLeft: '2px' },
  input: {
    width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #ddd',
    borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box',
  },
  inputFilled: {
    width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #27ae60',
    borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box', background: '#f0fff4',
  },
  select: {
    width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #ddd',
    borderRadius: '4px', fontSize: '0.9rem', background: '#fff', boxSizing: 'border-box',
  },
  textarea: {
    width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #ddd',
    borderRadius: '4px', fontSize: '0.9rem', resize: 'vertical', minHeight: '65px', boxSizing: 'border-box',
  },
  textareaFilled: {
    width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #27ae60',
    borderRadius: '4px', fontSize: '0.9rem', resize: 'vertical', minHeight: '65px', boxSizing: 'border-box', background: '#f0fff4',
  },
  error: {
    background: '#f8d7da', color: '#721c24', padding: '0.7rem 1rem',
    borderRadius: '4px', fontSize: '0.875rem', borderLeft: '4px solid #e74c3c',
    marginBottom: '0.75rem',
  },
  siretRow: { display: 'flex', gap: '0.5rem', alignItems: 'flex-end' },
  siretWrap: { flex: 1 },
  lookupBtn: (loading) => ({
    padding: '0.6rem 1rem',
    background: loading ? '#95a5a6' : '#3498db',
    color: '#fff', border: 'none', borderRadius: '4px',
    cursor: loading ? 'not-allowed' : 'pointer',
    fontSize: '0.875rem', fontWeight: 600, whiteSpace: 'nowrap',
    height: '38px', display: 'flex', alignItems: 'center', gap: '0.35rem',
  }),
  siretBanner: (ok) => ({
    display: 'flex', alignItems: 'center', gap: '0.5rem',
    padding: '0.5rem 0.75rem', borderRadius: '4px',
    fontSize: '0.82rem', marginTop: '0.4rem',
    background: ok ? '#d4edda' : '#fff3cd',
    color:      ok ? '#155724' : '#856404',
    border:     `1px solid ${ok ? '#c3e6cb' : '#ffeeba'}`,
  }),
  roleBadge: {
    Admin:       { background: '#d4edda', color: '#155724' },
    Utilisateur: { background: '#d1ecf1', color: '#0c5460' },
    Lecteur:     { background: '#fff3cd', color: '#856404' },
  },
};

// ─── Utilitaires SIRET ────────────────────────────────────────────────────────
const FORMES_JURIDIQUES_MAP = {
  '1000':'EI','2110':'SARL','2120':'SARL','2210':'SA','2220':'SA',
  '5306':'SASU','5307':'SAS','5308':'SAS','5485':'EURL','5498':'EURL',
  '5499':'SARL','5370':'SNC','6317':'Association','9220':'Association',
};
function getFormeJuridique(code) {
  if (!code) return '';
  if (FORMES_JURIDIQUES_MAP[code]) return FORMES_JURIDIQUES_MAP[code];
  const p = String(code).substring(0,2);
  if (p==='53') return 'SAS'; if (p==='54') return 'SCS';
  if (p==='21'||p==='51') return 'SARL'; if (p==='22'||p==='23') return 'SA';
  if (p==='10') return 'EI'; if (p==='92'||p==='63') return 'Association';
  return 'Autre';
}
function capitalizeWords(str) {
  if (!str) return '';
  return str.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}

// ─── Composant Field ──────────────────────────────────────────────────────────
function Field({ label, required, children }) {
  return (
    <div>
      <label style={S.label}>{label}{required && <span style={S.required}>*</span>}</label>
      {children}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ONGLET ENTREPRISES
// ═══════════════════════════════════════════════════════════════════════════════
const EMPTY_ENT = {
  nom: '', adresse: '', code_postal: '',
  ville: '', forme_juridique: '', siret: '', representant: '',
  email: '', telephone: '',
  afficher_calendrier: true, couleur_calendrier: '#3498db',
};

// Couleurs proposées pour le calendrier
const COULEURS_CALENDRIER = [
  '#3498db', '#e67e22', '#2ecc71', '#9b59b6', '#e74c3c',
  '#1abc9c', '#f39c12', '#34495e', '#16a085', '#8e44ad',
  '#c0392b', '#2980b9', '#27ae60', '#d35400', '#7f8c8d',
];

function OngletEntreprises() {
  const [entreprises, setEntreprises] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [showModal, setShowModal]     = useState(false);
  const [editEnt, setEditEnt]         = useState(null);
  const [form, setForm]               = useState(EMPTY_ENT);
  const [saving, setSaving]           = useState(false);
  const [formError, setFormError]     = useState('');
  const [siretLoading, setSiretLoading] = useState(false);
  const [siretStatus, setSiretStatus]   = useState(null);
  const [siretMsg, setSiretMsg]         = useState('');
  const [autoFilled, setAutoFilled]     = useState(false);
  // Logo
  const [logoPreview, setLogoPreview]   = useState(null);
  const [logoFile, setLogoFile]         = useState(null);
  const [logoError, setLogoError]       = useState('');
  const logoInputRef = useRef(null);
  // Papier en-tête
  const [entetePreview, setEntetePreview] = useState(null); // URL ou nom du fichier existant
  const [enteteFile, setEnteteFile]       = useState(null);
  const [enteteError, setEnteteError]     = useState('');
  const enteteInputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => { fetchEntreprises(); }, []);

  const fetchEntreprises = async () => {
    try {
      const r = await axios.get('/api/entreprises');
      setEntreprises(r.data);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditEnt(null); setForm(EMPTY_ENT); setFormError('');
    setSiretStatus(null); setSiretMsg(''); setAutoFilled(false);
    setLogoPreview(null); setLogoFile(null); setLogoError('');
    setEntetePreview(null); setEnteteFile(null); setEnteteError('');
    setShowModal(true);
  };

  const openEdit = (e) => {
    setEditEnt(e);
    setForm({ codification: e.codification, nom: e.nom || '', adresse: e.adresse || '',
      code_postal: e.code_postal || '', ville: e.ville || '',
      forme_juridique: e.forme_juridique || '', siret: e.siret || '', representant: e.representant || '',
      email: e.email || '', telephone: e.telephone ? formatPhone(e.telephone) : '',
      afficher_calendrier: e.afficher_calendrier !== false,
      couleur_calendrier: e.couleur_calendrier || '#3498db' });
    setFormError(''); setSiretStatus(null); setSiretMsg(''); setAutoFilled(false);
    // Afficher le logo existant s'il y en a un
    const logoBase = window.location.origin.includes('localhost') ? 'http://localhost:3001' : window.location.origin;
    setLogoPreview(e.logo_path ? `${logoBase}/${e.logo_path}` : null);
    setLogoFile(null); setLogoError('');
    setEntetePreview(e.papier_entete_path || null);
    setEnteteFile(null); setEnteteError('');
    setShowModal(true);
  };

  const closeModal = () => { if (!saving) setShowModal(false); };

  // ── Lookup SIRET ────────────────────────────────────────────────────────────
  const lookupSiret = async (siret) => {
    const clean = String(siret).replace(/\s/g,'');
    if (clean.length !== 14) {
      setSiretStatus('error'); setSiretMsg('Le SIRET doit comporter 14 chiffres.'); return;
    }
    setSiretLoading(true); setSiretStatus(null); setSiretMsg('');
    try {
      const url = `https://recherche-entreprises.api.gouv.fr/search?q=${clean}&page=1&per_page=1`;
      const resp = await fetch(url);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      if (!data.results || data.results.length === 0) {
        setSiretStatus('error'); setSiretMsg('Aucune entreprise trouvée pour ce SIRET.'); return;
      }
      const entreprise = data.results[0];
      const etab = entreprise.matching_etablissements?.find(e => e.siret === clean)
                || entreprise.matching_etablissements?.[0];

      const nom   = entreprise.nom_complet || '';
      const forme = getFormeJuridique(entreprise.nature_juridique || '');
      let adresse = '', code_postal = '', ville = '';
      if (etab) {
        const num = etab.numero_voie || '', type = etab.type_voie || '', lib = etab.libelle_voie || '';
        const compl = etab.complement_adresse || '';
        adresse = [compl, `${num} ${type} ${lib}`].filter(Boolean).map(s=>s.trim()).filter(Boolean).join(', ');
        code_postal = etab.code_postal || '';
        ville = capitalizeWords(etab.libelle_commune || '');
      }

      setForm(prev => ({
        ...prev,
        nom:             nom || prev.nom,
        adresse:         adresse || prev.adresse,
        code_postal:     code_postal || prev.code_postal,
        ville:           ville || prev.ville,
        forme_juridique: forme || prev.forme_juridique,
      }));
      setAutoFilled(true);
      setSiretStatus('ok');
      setSiretMsg(`✓ ${nom} — données pré-remplies depuis le Registre National des Entreprises`);
    } catch(err) {
      setSiretStatus('error');
      setSiretMsg('Impossible de contacter l\'API. Renseignez les champs manuellement.');
    } finally { setSiretLoading(false); }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'telephone') {
      setForm(prev => ({ ...prev, telephone: formatPhone(value) }));
      return;
    }
    setForm(prev => ({ ...prev, [name]: value }));
    if (name === 'siret') {
      setAutoFilled(false); setSiretStatus(null); setSiretMsg('');
      const clean = value.replace(/\s/g,'');
      if (clean.length === 14) {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => lookupSiret(clean), 400);
      }
    }
  };

  // ── Upload logo ─────────────────────────────────────────────────────────────
  const handleLogoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ['image/png','image/jpeg','image/jpg','image/svg+xml','image/webp'];
    if (!allowed.includes(file.type)) {
      setLogoError('Format non autorisé. Utilisez PNG, JPG, SVG ou WEBP.'); return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setLogoError('Fichier trop volumineux (max 2 Mo).'); return;
    }
    setLogoError('');
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleLogoUpload = async (codification) => {
    if (!logoFile) return;
    try {
      const fd = new FormData();
      fd.append('logo', logoFile);
      await axios.post(`/api/entreprises/${codification}/logo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    } catch(err) {
      console.error('Erreur upload logo:', err);
    }
  };

  const handleLogoDelete = async () => {
    if (!editEnt) { setLogoPreview(null); setLogoFile(null); return; }
    try {
      await axios.delete(`/api/entreprises/${editEnt.codification}/logo`);
      setLogoPreview(null); setLogoFile(null);
      fetchEntreprises();
    } catch(err) { setLogoError('Erreur lors de la suppression du logo.'); }
  };

  // ── Papier en-tête (PDF) ─────────────────────────────────────────────────────
  const handleEnteteSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      setEnteteError('Seuls les fichiers PDF sont acceptés.'); return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setEnteteError('Fichier trop volumineux (max 5 Mo).'); return;
    }
    setEnteteError('');
    setEnteteFile(file);
    setEntetePreview(file.name);
  };

  const handleEnteteUpload = async (codification) => {
    if (!enteteFile) return;
    try {
      const fd = new FormData();
      fd.append('entete', enteteFile);
      await axios.post(`/api/entreprises/${codification}/entete`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    } catch(err) {
      console.error('Erreur upload papier en-tête:', err);
    }
  };

  const handleEnteteDelete = async () => {
    if (!editEnt) { setEntetePreview(null); setEnteteFile(null); return; }
    try {
      await axios.delete(`/api/entreprises/${editEnt.codification}/entete`);
      setEntetePreview(null); setEnteteFile(null);
      fetchEntreprises();
    } catch(err) { setEnteteError('Erreur lors de la suppression.'); }
  };

  const handleEnteteView = () => {
    if (!editEnt || !entetePreview || enteteFile) return;
    const base = window.location.origin.includes('localhost') ? 'http://localhost:3001' : window.location.origin;
    window.open(`${base}/${entetePreview}`, '_blank');
  };

  const handleSubmit = async () => {
    setFormError('');
    if (!form.nom.trim()) { setFormError('Le nom est obligatoire.'); return; }
    setSaving(true);
    try {
      if (editEnt) {
        await axios.put(`/api/entreprises/${editEnt.codification}`, form);
        if (logoFile) await handleLogoUpload(editEnt.codification);
        if (enteteFile) await handleEnteteUpload(editEnt.codification);
      } else {
        const resp = await axios.post('/api/entreprises', form);
        if (logoFile && resp.data.codification) await handleLogoUpload(resp.data.codification);
        if (enteteFile && resp.data.codification) await handleEnteteUpload(resp.data.codification);
      }
      await fetchEntreprises();
      setShowModal(false);
    } catch(err) {
      setFormError(err.response?.data?.details || err.response?.data?.error || 'Erreur lors de l\'enregistrement.');
    } finally { setSaving(false); }
  };

  const handleDelete = async (codification) => {
    if (!window.confirm(`Supprimer l'entité "${codification}" ?`)) return;
    try {
      await axios.delete(`/api/entreprises/${codification}`);
      fetchEntreprises();
    } catch(err) {
      alert(err.response?.data?.error || 'Erreur lors de la suppression.');
    }
  };

  const inp  = (f) => autoFilled && form[f] ? S.inputFilled  : S.input;
  const ta   = (f) => autoFilled && form[f] ? S.textareaFilled : S.textarea;

  if (loading) return <div className="loading">Chargement…</div>;

  return (
    <div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Entités du groupe</h2>
          <button className="btn btn-primary" onClick={openCreate}>+ Nouvelle entité</button>
        </div>

        <table>
          <thead>
            <tr><th>Logo</th><th>Codification</th><th>Nom</th><th>Ville</th><th>Forme juridique</th><th>SIRET</th><th>Représentant</th><th style={{ textAlign:'center' }}>En-tête</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {entreprises.length === 0 ? (
              <tr><td colSpan="9" style={{ textAlign:'center', padding:'2rem' }}>Aucune entité</td></tr>
            ) : entreprises.map(e => (
              <tr key={e.codification}>
                <td style={{ width:'60px', textAlign:'center' }}>
                  {e.logo_path
                    ? <img src={`${window.location.origin.includes('localhost') ? 'http://localhost:3001' : window.location.origin}/${e.logo_path}`} alt="logo"
                        style={{ height:'36px', maxWidth:'56px', objectFit:'contain', borderRadius:'3px' }} />
                    : <span style={{ color:'#bdc3c7', fontSize:'1.2rem' }}>—</span>
                  }
                </td>
                <td><strong>{e.codification}</strong></td>
                <td>{e.nom}</td>
                <td>{e.ville || '—'}</td>
                <td>{e.forme_juridique || '—'}</td>
                <td><code style={{ fontSize:'0.8rem' }}>{e.siret || '—'}</code></td>
                <td>{e.representant || '—'}</td>
                <td style={{ textAlign:'center' }}>
                  {e.papier_entete_path
                    ? <span title="Papier en-tête importé" style={{ color:'#27ae60', fontSize:'1.1rem', cursor:'pointer' }}
                        onClick={() => {
                          const base = window.location.origin.includes('localhost') ? 'http://localhost:3001' : window.location.origin;
                          window.open(`${base}/${e.papier_entete_path}`, '_blank');
                        }}>📄</span>
                    : <span style={{ color:'#bdc3c7', fontSize:'1.1rem' }}>—</span>
                  }
                </td>
                <td>
                  <div className="actions">
                    <button onClick={() => openEdit(e)} className="btn btn-primary">Modifier</button>
                    <button onClick={() => handleDelete(e.codification)} className="btn btn-danger">Supprimer</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Modale ── */}
      {showModal && (
        <div style={S.overlay} onClick={closeModal}>
          <div style={S.dialog} onClick={e => e.stopPropagation()}>
            <div style={S.dialogHeader}>
              <h2 style={S.dialogTitle}>{editEnt ? `Modifier — ${editEnt.nom}` : 'Nouvelle entité'}</h2>
              <button style={S.closeBtn} onClick={closeModal}>×</button>
            </div>

            <div style={S.dialogBody}>
              {formError && <div style={S.error}>{formError}</div>}

              {/* SIRET en premier */}
              <div style={S.sectionTitle}>Recherche automatique par SIRET</div>
              <div style={S.siretRow}>
                <div style={S.siretWrap}>
                  <Field label="SIRET">
                    <input style={siretStatus==='ok' ? S.inputFilled : S.input}
                      name="siret" value={form.siret} onChange={handleChange}
                      placeholder="14 chiffres" maxLength={14} />
                  </Field>
                </div>
                <button style={S.lookupBtn(siretLoading)}
                  onClick={() => lookupSiret(form.siret)} disabled={siretLoading}>
                  {siretLoading
                    ? <><span style={{ display:'inline-block', width:'12px', height:'12px', border:'2px solid rgba(255,255,255,0.4)', borderTopColor:'#fff', borderRadius:'50%', animation:'spin 0.7s linear infinite' }} /> Recherche…</>
                    : <>🔍 Rechercher</>
                  }
                </button>
              </div>
              {siretMsg && <div style={S.siretBanner(siretStatus==='ok')}>{siretMsg}</div>}
              {autoFilled && (
                <div style={{ fontSize:'0.78rem', color:'#27ae60', margin:'0.5rem 0 0.25rem', display:'flex', gap:'0.4rem' }}>
                  <span>🟢</span><span>Champs surlignés remplis automatiquement — modifiables si nécessaire.</span>
                </div>
              )}

              {/* Identification */}
              <div style={S.sectionTitle}>Identification</div>
              {editEnt && (
                <div style={{ background:'#f8f9fa', padding:'0.6rem 1rem', borderRadius:'4px', marginBottom:'0.75rem', fontSize:'0.85rem', color:'#7f8c8d' }}>
                  Codification : <strong style={{ color:'#2c3e50' }}>{editEnt.codification}</strong>
                </div>
              )}
              <Field label="Nom / Raison sociale" required>
                <input style={inp('nom')} name="nom" value={form.nom}
                  onChange={handleChange} placeholder="Raison sociale" />
              </Field>

              {/* Coordonnées */}
              <div style={S.sectionTitle}>Coordonnées</div>
              <div style={{ marginBottom:'0.75rem' }}>
                <Field label="Adresse">
                  <textarea style={ta('adresse')} name="adresse" value={form.adresse}
                    onChange={handleChange} placeholder="Numéro, rue…" rows={2} />
                </Field>
              </div>
              <div style={S.grid2}>
                <Field label="Code postal">
                  <input style={inp('code_postal')} name="code_postal" value={form.code_postal}
                    onChange={handleChange} placeholder="75000" maxLength={10} />
                </Field>
                <Field label="Ville">
                  <input style={inp('ville')} name="ville" value={form.ville}
                    onChange={handleChange} placeholder="Paris" />
                </Field>
              </div>
              <div style={{ ...S.grid2, marginTop: '0.75rem' }}>
                <Field label="Téléphone">
                  <input style={S.input} name="telephone" value={form.telephone}
                    onChange={handleChange} placeholder="01 23 45 67 89" maxLength={14} />
                </Field>
                <Field label="Email">
                  <input style={S.input} name="email" value={form.email}
                    onChange={handleChange} placeholder="contact@entreprise.fr" type="email" />
                </Field>
              </div>

              {/* Juridique */}
              <div style={S.sectionTitle}>Informations juridiques</div>
              <div style={S.grid2}>
                <Field label="Forme juridique">
                  <select style={autoFilled && form.forme_juridique
                    ? { ...S.select, border:'1px solid #27ae60', background:'#f0fff4' } : S.select}
                    name="forme_juridique" value={form.forme_juridique} onChange={handleChange}>
                    <option value="">— Sélectionner —</option>
                    {['SARL','SAS','SA','SASU','EURL','SNC','EI / Auto-entrepreneur','Association','Autre'].map(f =>
                      <option key={f} value={f}>{f}</option>
                    )}
                  </select>
                </Field>
                <Field label="Représentant">
                  <input style={S.input} name="representant" value={form.representant}
                    onChange={handleChange} placeholder="Prénom Nom" />
                </Field>
              </div>

              {/* Calendrier */}
              <div style={S.sectionTitle}>Calendrier des DLRO</div>
              <Field label="Afficher cette entité dans le calendrier">
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button type="button"
                    onClick={() => setForm(p => ({ ...p, afficher_calendrier: true }))}
                    style={{
                      flex: 1, padding: '0.6rem', borderRadius: '6px', fontWeight: 600, cursor: 'pointer',
                      fontSize: '0.9rem', border: '2px solid',
                      borderColor: form.afficher_calendrier ? '#27ae60' : '#ddd',
                      background: form.afficher_calendrier ? '#eafaf1' : '#fff',
                      color: form.afficher_calendrier ? '#1e8449' : '#7f8c8d',
                    }}>
                    ✓ Oui
                  </button>
                  <button type="button"
                    onClick={() => setForm(p => ({ ...p, afficher_calendrier: false }))}
                    style={{
                      flex: 1, padding: '0.6rem', borderRadius: '6px', fontWeight: 600, cursor: 'pointer',
                      fontSize: '0.9rem', border: '2px solid',
                      borderColor: !form.afficher_calendrier ? '#e74c3c' : '#ddd',
                      background: !form.afficher_calendrier ? '#fdedec' : '#fff',
                      color: !form.afficher_calendrier ? '#c0392b' : '#7f8c8d',
                    }}>
                    Non
                  </button>
                </div>
              </Field>

              {form.afficher_calendrier && (
                <Field label="Couleur dans le calendrier">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                      {COULEURS_CALENDRIER.map(c => (
                        <button key={c} type="button" title={c}
                          onClick={() => setForm(p => ({ ...p, couleur_calendrier: c }))}
                          style={{
                            width: '28px', height: '28px', borderRadius: '50%', cursor: 'pointer',
                            background: c, padding: 0,
                            border: form.couleur_calendrier === c ? '3px solid #2c3e50' : '2px solid #fff',
                            boxShadow: '0 0 0 1px #ddd',
                          }} />
                      ))}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <input type="color" name="couleur_calendrier" value={form.couleur_calendrier}
                        onChange={handleChange}
                        style={{ width: '40px', height: '32px', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer', padding: '2px' }} />
                      <span style={{ fontSize: '0.82rem', color: '#7f8c8d', fontFamily: 'monospace' }}>
                        {form.couleur_calendrier}
                      </span>
                    </div>
                  </div>
                </Field>
              )}

              {/* Logo */}
              <div style={S.sectionTitle}>Logo de l'entreprise</div>
              <div style={{ display:'flex', alignItems:'center', gap:'1.5rem' }}>

                {/* Zone de prévisualisation */}
                <div
                  onClick={() => logoInputRef.current?.click()}
                  style={{
                    width:'110px', height:'110px', borderRadius:'8px', flexShrink:0,
                    border: logoPreview ? '2px solid #27ae60' : '2px dashed #bdc3c7',
                    display:'flex', alignItems:'center', justifyContent:'center',
                    cursor:'pointer', background: logoPreview ? '#fff' : '#f8f9fa',
                    overflow:'hidden', position:'relative',
                    transition:'border-color 0.2s',
                  }}
                  title="Cliquer pour choisir un logo"
                >
                  {logoPreview
                    ? <img src={logoPreview} alt="Logo" style={{ maxWidth:'100%', maxHeight:'100%', objectFit:'contain' }} />
                    : <div style={{ textAlign:'center', color:'#95a5a6', fontSize:'0.78rem', padding:'0.5rem' }}>
                        <div style={{ fontSize:'1.8rem', marginBottom:'0.25rem' }}>🖼️</div>
                        <div>Cliquer pour<br/>ajouter un logo</div>
                      </div>
                  }
                </div>

                {/* Infos + boutons */}
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:'0.82rem', color:'#7f8c8d', marginBottom:'0.75rem', lineHeight:1.5 }}>
                    Formats acceptés : <strong>PNG, JPG, SVG, WEBP</strong><br/>
                    Taille maximale : <strong>2 Mo</strong>
                  </div>
                  <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap' }}>
                    <button type="button" className="btn btn-primary"
                      onClick={() => logoInputRef.current?.click()}
                      style={{ fontSize:'0.82rem', padding:'0.4rem 0.9rem' }}>
                      📁 {logoPreview ? 'Changer' : 'Choisir un fichier'}
                    </button>
                    {logoPreview && (
                      <button type="button" className="btn btn-danger"
                        onClick={handleLogoDelete}
                        style={{ fontSize:'0.82rem', padding:'0.4rem 0.9rem' }}>
                        🗑️ Supprimer
                      </button>
                    )}
                  </div>
                  {logoError && (
                    <div style={{ marginTop:'0.5rem', fontSize:'0.8rem', color:'#e74c3c' }}>
                      ⚠️ {logoError}
                    </div>
                  )}
                  {logoFile && (
                    <div style={{ marginTop:'0.4rem', fontSize:'0.78rem', color:'#27ae60' }}>
                      ✓ {logoFile.name} sélectionné — sera enregistré à la validation
                    </div>
                  )}
                </div>
              </div>

              {/* Papier en-tête */}
              <div style={S.sectionTitle}>Papier en-tête (PDF)</div>
              <div style={{ display:'flex', alignItems:'center', gap:'1.5rem' }}>

                {/* Zone icône/aperçu */}
                <div
                  onClick={() => enteteInputRef.current?.click()}
                  style={{
                    width:'110px', height:'110px', borderRadius:'8px', flexShrink:0,
                    border: entetePreview ? '2px solid #3498db' : '2px dashed #bdc3c7',
                    display:'flex', alignItems:'center', justifyContent:'center',
                    cursor:'pointer', background: entetePreview ? '#eaf2fb' : '#f8f9fa',
                    overflow:'hidden', position:'relative',
                    transition:'border-color 0.2s',
                  }}
                  title="Cliquer pour choisir un PDF"
                >
                  {entetePreview
                    ? <div style={{ textAlign:'center', padding:'0.5rem' }}>
                        <div style={{ fontSize:'2.2rem', marginBottom:'0.2rem' }}>📄</div>
                        <div style={{ fontSize:'0.65rem', color:'#2c3e50', wordBreak:'break-all', lineHeight:1.2 }}>
                          {enteteFile ? enteteFile.name : 'PDF importé'}
                        </div>
                      </div>
                    : <div style={{ textAlign:'center', color:'#95a5a6', fontSize:'0.78rem', padding:'0.5rem' }}>
                        <div style={{ fontSize:'1.8rem', marginBottom:'0.25rem' }}>📄</div>
                        <div>Cliquer pour<br/>importer un PDF</div>
                      </div>
                  }
                </div>

                {/* Infos + boutons */}
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:'0.82rem', color:'#7f8c8d', marginBottom:'0.75rem', lineHeight:1.5 }}>
                    Importez votre <strong>papier en-tête au format PDF</strong>.<br/>
                    Il sera utilisé comme fond de page pour la génération des contrats.<br/>
                    Taille maximale : <strong>5 Mo</strong>
                  </div>
                  <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap' }}>
                    <button type="button" className="btn btn-primary"
                      onClick={() => enteteInputRef.current?.click()}
                      style={{ fontSize:'0.82rem', padding:'0.4rem 0.9rem' }}>
                      📁 {entetePreview ? 'Remplacer' : 'Choisir un PDF'}
                    </button>
                    {entetePreview && !enteteFile && editEnt && (
                      <button type="button" className="btn btn-secondary"
                        onClick={handleEnteteView}
                        style={{ fontSize:'0.82rem', padding:'0.4rem 0.9rem' }}>
                        👁️ Visualiser
                      </button>
                    )}
                    {entetePreview && (
                      <button type="button" className="btn btn-danger"
                        onClick={handleEnteteDelete}
                        style={{ fontSize:'0.82rem', padding:'0.4rem 0.9rem' }}>
                        🗑️ Supprimer
                      </button>
                    )}
                  </div>
                  {enteteError && (
                    <div style={{ marginTop:'0.5rem', fontSize:'0.8rem', color:'#e74c3c' }}>
                      ⚠️ {enteteError}
                    </div>
                  )}
                  {enteteFile && (
                    <div style={{ marginTop:'0.4rem', fontSize:'0.78rem', color:'#27ae60' }}>
                      ✓ {enteteFile.name} sélectionné — sera enregistré à la validation
                    </div>
                  )}
                  {entetePreview && !enteteFile && editEnt && (
                    <div style={{ marginTop:'0.4rem', fontSize:'0.78rem', color:'#3498db' }}>
                      ✓ Papier en-tête déjà importé
                    </div>
                  )}
                </div>
              </div>

              {/* Inputs file cachés */}
              <input ref={enteteInputRef} type="file" accept=".pdf"
                style={{ display:'none' }} onChange={handleEnteteSelect} />
              <input ref={logoInputRef} type="file" accept=".png,.jpg,.jpeg,.svg,.webp"
                style={{ display:'none' }} onChange={handleLogoSelect} />
            </div>

            <div style={S.dialogFooter}>
              <button className="btn btn-secondary" onClick={closeModal} disabled={saving}>Annuler</button>
              <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
                {saving ? 'Enregistrement…' : editEnt ? 'Enregistrer' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ONGLET UTILISATEURS (inchangé)
// ═══════════════════════════════════════════════════════════════════════════════
const ROLES = ['Admin', 'Utilisateur', 'Lecteur'];
const EMPTY_USR = { prenom:'', nom:'', email:'', password:'', role:'Utilisateur', entreprise_codification:'' };

function isValidEmail(email) {
  if (!email) return true;
  const re = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  return re.test(email);
}

function OngletUtilisateurs({ currentUser }) {
  const [users, setUsers]             = useState([]);
  const [entreprises, setEntreprises] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [showModal, setShowModal]     = useState(false);
  const [editUser, setEditUser]       = useState(null);
  const [form, setForm]               = useState(EMPTY_USR);
  const [saving, setSaving]           = useState(false);
  const [formError, setFormError]     = useState('');

  useEffect(() => { fetchUsers(); fetchEntreprises(); }, []);

  const fetchUsers = async () => {
    try { const r = await axios.get('/api/utilisateurs'); setUsers(r.data); }
    catch(e) { console.error(e); } finally { setLoading(false); }
  };
  const fetchEntreprises = async () => {
    try { const r = await axios.get('/api/entreprises'); setEntreprises(r.data); }
    catch(e) { console.error(e); }
  };

  const openCreate = () => { setEditUser(null); setForm(EMPTY_USR); setFormError(''); setShowModal(true); };
  const openEdit = (u) => {
    setEditUser(u);
    setForm({ prenom: u.prenom, nom: u.nom, email: u.email || '',
      password: '', role: u.role, entreprise_codification: u.entreprise_codification || '' });
    setFormError(''); setShowModal(true);
  };
  const closeModal = () => { if (!saving) setShowModal(false); };
  const handleChange = (e) => { const {name,value}=e.target; setForm(p=>({...p,[name]:value})); };

  const handleSubmit = async () => {
    setFormError('');
    if (!form.prenom.trim() || !form.nom.trim()) { setFormError('Prénom et nom sont obligatoires.'); return; }
    if (!editUser && !form.password.trim()) { setFormError('Le mot de passe est obligatoire.'); return; }
    if (form.email && !isValidEmail(form.email)) { setFormError('Adresse email invalide. Format attendu : exemple@domaine.fr'); return; }
    setSaving(true);
    try {
      if (editUser) await axios.put('/api/utilisateurs/' + editUser.identifiant, form);
      else          await axios.post('/api/utilisateurs', form);
      await fetchUsers(); setShowModal(false);
    } catch(err) {
      setFormError(err.response?.data?.error || "Erreur lors de l'enregistrement.");
    } finally { setSaving(false); }
  };

  const handleDelete = async (identifiant) => {
    if (identifiant === currentUser.identifiant) { alert('Vous ne pouvez pas supprimer votre propre compte.'); return; }
    if (!window.confirm(`Supprimer l'utilisateur "${identifiant}" ?`)) return;
    try { await axios.delete(`/api/utilisateurs/${identifiant}`); fetchUsers(); }
    catch(err) { alert(err.response?.data?.error || 'Erreur.'); }
  };

  if (loading) return <div className="loading">Chargement…</div>;

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Utilisateurs</h2>
          <button className="btn btn-primary" onClick={openCreate}>+ Nouvel utilisateur</button>
        </div>
        <table>
          <thead>
            <tr><th>Identifiant</th><th>Nom</th><th>Email</th><th>Rôle</th><th>Entité</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr><td colSpan="6" style={{ textAlign:'center', padding:'2rem' }}>Aucun utilisateur</td></tr>
            ) : users.map(u => (
              <tr key={u.identifiant}>
                <td><strong>{u.identifiant}</strong></td>
                <td>{u.prenom} {u.nom}</td>
                <td style={{ fontSize:'0.85rem' }}>{u.email || <em style={{ color:'#aaa' }}>—</em>}</td>
                <td><span className="badge" style={S.roleBadge[u.role]||{}}>{u.role}</span></td>
                <td>{u.entreprise_nom || <em style={{ color:'#aaa' }}>—</em>}</td>
                <td>
                  <div className="actions">
                    <button onClick={() => openEdit(u)} className="btn btn-primary">Modifier</button>
                    <button onClick={() => handleDelete(u.identifiant)} className="btn btn-danger"
                      disabled={u.identifiant === currentUser.identifiant}>Supprimer</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div style={S.overlay} onClick={closeModal}>
          <div style={S.dialog} onClick={e => e.stopPropagation()}>
            <div style={S.dialogHeader}>
              <h2 style={S.dialogTitle}>{editUser ? `Modifier — ${editUser.identifiant}` : 'Nouvel utilisateur'}</h2>
              <button style={S.closeBtn} onClick={closeModal}>×</button>
            </div>
            <div style={S.dialogBody}>
              {formError && <div style={S.error}>{formError}</div>}

              {editUser && (
                <div style={{ background:'#f8f9fa', padding:'0.6rem 1rem', borderRadius:'4px', marginBottom:'0.5rem', fontSize:'0.85rem', color:'#7f8c8d' }}>
                  Identifiant : <strong style={{ color:'#2c3e50' }}>{editUser.identifiant}</strong>
                </div>
              )}

              <div style={S.grid2}>
                <Field label="Prénom" required>
                  <input style={S.input} name="prenom" value={form.prenom} onChange={handleChange} placeholder="Prénom" />
                </Field>
                <Field label="Nom" required>
                  <input style={S.input} name="nom" value={form.nom} onChange={handleChange} placeholder="Nom" />
                </Field>
              </div>
              <div style={{ marginTop:'1rem' }}>
                <Field label="Adresse email">
                  <input style={{
                    ...S.input,
                    borderColor: form.email && !isValidEmail(form.email) ? '#e74c3c' : '#ddd'
                  }}
                    type="email" name="email" value={form.email} onChange={handleChange}
                    placeholder="exemple@domaine.fr" />
                  {form.email && !isValidEmail(form.email) && (
                    <div style={{ fontSize:'0.78rem', color:'#e74c3c', marginTop:'0.25rem' }}>
                      ⚠ Format email invalide
                    </div>
                  )}
                </Field>
              </div>
              <div style={{ ...S.grid2, marginTop:'1rem' }}>
                <Field label="Rôle" required>
                  <select style={S.select} name="role" value={form.role} onChange={handleChange}>
                    {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </Field>
                <Field label="Entité">
                  <select style={S.select} name="entreprise_codification"
                    value={form.entreprise_codification} onChange={handleChange}>
                    <option value="">— Aucune entité —</option>
                    {entreprises.map(e => (
                      <option key={e.codification} value={e.codification}>{e.nom} ({e.codification})</option>
                    ))}
                  </select>
                </Field>
              </div>
              <div style={{ marginTop:'1rem' }}>
                <Field label={editUser ? 'Nouveau mot de passe (vide = inchangé)' : 'Mot de passe'} required={!editUser}>
                  <input style={S.input} type="password" name="password" value={form.password}
                    onChange={handleChange} placeholder={editUser ? 'Laisser vide pour ne pas changer' : '••••••••'} />
                </Field>
              </div>
            </div>
            <div style={S.dialogFooter}>
              <button className="btn btn-secondary" onClick={closeModal} disabled={saving}>Annuler</button>
              <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
                {saving ? 'Enregistrement…' : editUser ? 'Enregistrer' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ONGLET TYPES DE DOCUMENTS
// ═══════════════════════════════════════════════════════════════════════════════
function OngletTypesDocuments() {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ type: '', obligatoire: false, duree_validite_mois: '' });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [showAdd, setShowAdd] = useState(false);

  useEffect(() => { fetchTypes(); }, []);

  const fetchTypes = async () => {
    try {
      const r = await axios.get('/api/documents/types');
      setTypes(r.data);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleAdd = async () => {
    setFormError('');
    if (!form.type.trim()) { setFormError('Le type est obligatoire'); return; }
    setSaving(true);
    try {
      await axios.post('/api/documents/types', {
        type: form.type,
        obligatoire: form.obligatoire,
        duree_validite_mois: form.duree_validite_mois ? parseInt(form.duree_validite_mois) : null,
      });
      setForm({ type: '', obligatoire: false, duree_validite_mois: '' });
      setShowAdd(false);
      await fetchTypes();
    } catch(err) {
      setFormError(err.response?.data?.error || 'Erreur');
    } finally { setSaving(false); }
  };

  const startEdit = (t) => {
    setEditId(t.id);
    setForm({ type: t.type, obligatoire: t.obligatoire || false, duree_validite_mois: t.duree_validite_mois || '' });
  };

  const cancelEdit = () => {
    setEditId(null);
    setForm({ type: '', obligatoire: false, duree_validite_mois: '' });
  };

  const handleSave = async (id) => {
    setSaving(true);
    try {
      await axios.put(`/api/documents/types/${id}`, {
        type: form.type,
        obligatoire: form.obligatoire,
        duree_validite_mois: form.duree_validite_mois ? parseInt(form.duree_validite_mois) : null,
      });
      setEditId(null);
      setForm({ type: '', obligatoire: false, duree_validite_mois: '' });
      await fetchTypes();
    } catch(err) {
      alert(err.response?.data?.error || 'Erreur');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce type de document ?')) return;
    try {
      await axios.delete(`/api/documents/types/${id}`);
      await fetchTypes();
    } catch(err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  if (loading) return <div className="loading">Chargement…</div>;

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Types de documents</h2>
          <button className="btn btn-primary" onClick={() => { setShowAdd(true); setForm({ type: '', obligatoire: false, duree_validite_mois: '' }); }}>
            + Nouveau type
          </button>
        </div>

        {/* Ligne d'ajout */}
        {showAdd && (
          <div style={{ padding: '1rem 1.5rem', background: '#f0f7ff', borderBottom: '1px solid #d1ecf1', display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: 2, minWidth: '200px' }}>
              <label style={S.label}>Type de document <span style={S.required}>*</span></label>
              <input style={S.input} value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))}
                placeholder="Ex : Attestation URSSAF" autoFocus
                onKeyDown={e => { if (e.key === 'Enter') handleAdd(); if (e.key === 'Escape') setShowAdd(false); }} />
            </div>
            <div style={{ minWidth: '100px' }}>
              <label style={S.label}>Durée validité</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                <input style={{ ...S.input, width: '70px' }} type="number" min="0"
                  value={form.duree_validite_mois} onChange={e => setForm(p => ({ ...p, duree_validite_mois: e.target.value }))}
                  placeholder="—" />
                <span style={{ fontSize: '0.82rem', color: '#7f8c8d' }}>mois</span>
              </div>
            </div>
            <div style={{ minWidth: '100px' }}>
              <label style={{ ...S.label, display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.obligatoire}
                  onChange={e => setForm(p => ({ ...p, obligatoire: e.target.checked }))} />
                Obligatoire
              </label>
            </div>
            <div style={{ display: 'flex', gap: '0.4rem' }}>
              <button className="btn btn-primary" onClick={handleAdd} disabled={saving}
                style={{ fontSize: '0.82rem', padding: '0.45rem 0.9rem' }}>
                {saving ? 'Ajout…' : '✓ Ajouter'}
              </button>
              <button className="btn btn-secondary" onClick={() => setShowAdd(false)}
                style={{ fontSize: '0.82rem', padding: '0.45rem 0.9rem' }}>
                Annuler
              </button>
            </div>
            {formError && <div style={{ ...S.error, width: '100%', marginTop: '0.5rem' }}>{formError}</div>}
          </div>
        )}

        <table>
          <thead>
            <tr>
              <th>Type de document</th>
              <th style={{ textAlign: 'center' }}>Obligatoire</th>
              <th style={{ textAlign: 'center' }}>Durée de validité</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {types.length === 0 ? (
              <tr><td colSpan="4" style={{ textAlign: 'center', padding: '2rem', color: '#95a5a6' }}>Aucun type de document</td></tr>
            ) : types.map(t => {
              const isEditing = editId === t.id;
              return (
                <tr key={t.id}>
                  <td>
                    {isEditing ? (
                      <input style={{ ...S.input, border: '2px solid #3498db', fontWeight: 600 }}
                        value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))}
                        autoFocus onKeyDown={e => { if (e.key === 'Enter') handleSave(t.id); if (e.key === 'Escape') cancelEdit(); }} />
                    ) : (
                      <strong>{t.type}</strong>
                    )}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {isEditing ? (
                      <input type="checkbox" checked={form.obligatoire}
                        onChange={e => setForm(p => ({ ...p, obligatoire: e.target.checked }))} />
                    ) : (
                      t.obligatoire
                        ? <span style={{ background: '#d4edda', color: '#155724', padding: '0.15rem 0.5rem', borderRadius: '10px', fontSize: '0.78rem', fontWeight: 600 }}>Oui</span>
                        : <span style={{ color: '#bdc3c7', fontSize: '0.85rem' }}>—</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {isEditing ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.3rem' }}>
                        <input style={{ ...S.input, width: '70px', textAlign: 'center' }} type="number" min="0"
                          value={form.duree_validite_mois} onChange={e => setForm(p => ({ ...p, duree_validite_mois: e.target.value }))} placeholder="—" />
                        <span style={{ fontSize: '0.82rem', color: '#7f8c8d' }}>mois</span>
                      </div>
                    ) : (
                      t.duree_validite_mois
                        ? <span style={{ fontWeight: 600 }}>{t.duree_validite_mois} mois</span>
                        : <span style={{ color: '#bdc3c7' }}>Permanent</span>
                    )}
                  </td>
                  <td>
                    <div className="actions">
                      {isEditing ? (
                        <>
                          <button className="btn btn-success" onClick={() => handleSave(t.id)} disabled={saving}
                            style={{ fontSize: '0.82rem', padding: '0.3rem 0.7rem' }}>✓</button>
                          <button className="btn btn-secondary" onClick={cancelEdit}
                            style={{ fontSize: '0.82rem', padding: '0.3rem 0.7rem' }}>✕</button>
                        </>
                      ) : (
                        <>
                          <button className="btn btn-primary" onClick={() => startEdit(t)}>Modifier</button>
                          <button className="btn btn-danger" onClick={() => handleDelete(t.id)}>Supprimer</button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ONGLET TYPES D'ACTIVITÉ
// ═══════════════════════════════════════════════════════════════════════════════
function OngletTypesActivite() {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ nom: '', categorie: '' });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [openCats, setOpenCats] = useState({});
  const [search, setSearch] = useState('');

  useEffect(() => { fetchTypes(); }, []);

  const fetchTypes = async () => {
    try {
      const r = await axios.get('/api/types-activite');
      setTypes(r.data);
      // Ouvrir toutes les categories par defaut
      const cats = {};
      r.data.forEach(t => { if (t.categorie) cats[t.categorie] = true; });
      if (!Object.keys(openCats).length) setOpenCats(cats);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  // Grouper par categorie
  const categories = [...new Set(types.map(t => t.categorie || 'SANS CATEGORIE'))].sort();
  const grouped = {};
  categories.forEach(c => { grouped[c] = []; });
  types.forEach(t => {
    const cat = t.categorie || 'SANS CATEGORIE';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(t);
  });

  // Filtrage par recherche
  const filteredGrouped = {};
  const s = search.trim().toLowerCase();
  categories.forEach(cat => {
    const filtered = s ? grouped[cat].filter(t => t.nom.toLowerCase().includes(s)) : grouped[cat];
    if (filtered.length > 0) filteredGrouped[cat] = filtered;
  });

  const toggleCat = (cat) => setOpenCats(prev => ({ ...prev, [cat]: !prev[cat] }));

  const handleAdd = async () => {
    setFormError('');
    if (!form.nom.trim()) { setFormError('Le nom est obligatoire'); return; }
    if (!form.categorie.trim()) { setFormError('La catégorie est obligatoire'); return; }
    setSaving(true);
    try {
      await axios.post('/api/types-activite', { nom: form.nom, categorie: form.categorie });
      setForm({ nom: '', categorie: '' });
      setShowAdd(false);
      await fetchTypes();
    } catch(err) {
      setFormError(err.response?.data?.error || 'Erreur');
    } finally { setSaving(false); }
  };

  const startEdit = (t) => {
    setEditId(t.id);
    setForm({ nom: t.nom, categorie: t.categorie || '' });
  };

  const cancelEdit = () => {
    setEditId(null);
    setForm({ nom: '', categorie: '' });
  };

  const handleSave = async (id) => {
    setSaving(true);
    try {
      await axios.put('/api/types-activite/' + id, { nom: form.nom, categorie: form.categorie });
      setEditId(null);
      setForm({ nom: '', categorie: '' });
      await fetchTypes();
    } catch(err) {
      alert(err.response?.data?.error || 'Erreur');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce type d\'activité ?')) return;
    try {
      await axios.delete('/api/types-activite/' + id);
      await fetchTypes();
    } catch(err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  if (loading) return <div className="loading">Chargement...</div>;

  const catColors = {
    'GROS OEUVRE / STRUCTURE': '#e74c3c',
    'CHARPENTE / COUVERTURE / FACADE': '#e67e22',
    'SECOND OEUVRE INTERIEUR': '#2ecc71',
    'TECHNIQUE / FLUIDES': '#3498db',
    'DEMOLITION / DEPOLLUTION': '#9b59b6',
    'EXTERIEUR / PAYSAGE': '#1abc9c',
    'SERVICES / DIVERS': '#95a5a6',
  };

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Types d'activité</h2>
          <input style={{ ...S.input, maxWidth: '250px', margin: '0 1rem', flex: '1' }}
            placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)} />
          <button className="btn btn-primary" onClick={() => setShowAdd(!showAdd)}>
            {showAdd ? 'Annuler' : '+ Nouveau type'}
          </button>
        </div>

        {showAdd && (
          <div style={{ padding: '1rem 1.5rem', background: '#f8f9fa', borderBottom: '1px solid #ecf0f1' }}>
            {formError && <div style={S.error}>{formError}</div>}
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <label style={S.label}>Catégorie <span style={S.required}>*</span></label>
                <select style={S.select} value={form.categorie}
                  onChange={e => setForm(f => ({ ...f, categorie: e.target.value }))}>
                  <option value="">-- Choisir --</option>
                  {categories.filter(c => c !== 'SANS CATEGORIE').map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={S.label}>Nom <span style={S.required}>*</span></label>
                <input style={S.input} value={form.nom}
                  onChange={e => setForm(f => ({ ...f, nom: e.target.value }))}
                  placeholder="Ex : ELECTRICITE, PLOMBERIE..."
                  onKeyDown={e => e.key === 'Enter' && handleAdd()} />
              </div>
              <button className="btn btn-primary" onClick={handleAdd} disabled={saving}
                style={{ height: '38px', whiteSpace: 'nowrap' }}>
                {saving ? '...' : 'Ajouter'}
              </button>
            </div>
          </div>
        )}

        <div style={{ padding: '0' }}>
          {Object.keys(filteredGrouped).length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#95a5a6' }}>
              {s ? 'Aucun résultat' : 'Aucun type d\'activité'}
            </div>
          ) : Object.entries(filteredGrouped).map(([cat, items]) => {
            const color = catColors[cat] || '#7f8c8d';
            const isOpen = openCats[cat] !== false;
            return (
              <div key={cat} style={{ borderBottom: '1px solid #ecf0f1' }}>
                <div onClick={() => toggleCat(cat)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.75rem',
                    padding: '0.75rem 1.25rem', cursor: 'pointer', background: '#f8f9fa',
                    userSelect: 'none',
                  }}>
                  <span style={{ fontSize: '0.8rem', color: '#7f8c8d', width: '1rem', textAlign: 'center' }}>
                    {isOpen ? '\u25BC' : '\u25B6'}
                  </span>
                  <span style={{
                    display: 'inline-block', width: '10px', height: '10px',
                    borderRadius: '50%', background: color, flexShrink: 0,
                  }} />
                  <span style={{ fontWeight: 600, fontSize: '0.9rem', color: '#2c3e50' }}>{cat}</span>
                  <span style={{
                    background: color + '22', color: color, fontSize: '0.75rem',
                    fontWeight: 600, padding: '0.15rem 0.5rem', borderRadius: '10px',
                  }}>
                    {items.length}
                  </span>
                </div>
                {isOpen && (
                  <div style={{ padding: '0' }}>
                    {items.map(t => (
                      <div key={t.id} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '0.5rem 1.25rem 0.5rem 3rem',
                        borderTop: '1px solid #f0f0f0',
                      }}>
                        {editId === t.id ? (
                          <div style={{ display: 'flex', gap: '0.5rem', flex: 1, alignItems: 'center' }}>
                            <input style={{ ...S.input, flex: 1 }} value={form.nom}
                              onChange={e => setForm(f => ({ ...f, nom: e.target.value }))}
                              onKeyDown={e => { if (e.key === 'Enter') handleSave(t.id); if (e.key === 'Escape') cancelEdit(); }}
                              autoFocus />
                            <button className="btn btn-primary" onClick={() => handleSave(t.id)} disabled={saving}
                              style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}>OK</button>
                            <button className="btn btn-secondary" onClick={cancelEdit}
                              style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}>Annuler</button>
                          </div>
                        ) : (
                          <>
                            <span style={{ fontSize: '0.9rem', color: '#34495e' }}>{t.nom}</span>
                            <div style={{ display: 'flex', gap: '0.35rem' }}>
                              <button className="btn btn-primary" onClick={() => startEdit(t)}
                                style={{ padding: '0.2rem 0.45rem', fontSize: '0.75rem' }}>Modifier</button>
                              <button className="btn btn-danger" onClick={() => handleDelete(t.id)}
                                style={{ padding: '0.2rem 0.45rem', fontSize: '0.75rem' }}>Supprimer</button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ padding: '0.75rem 1.25rem', background: '#f8f9fa', borderTop: '1px solid #ecf0f1',
          fontSize: '0.82rem', color: '#7f8c8d', display: 'flex', justifyContent: 'space-between' }}>
          <span>Total : {types.length} activités dans {categories.length} catégories</span>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PAGE ADMINISTRATION — avec onglets
// ═══════════════════════════════════════════════════════════════════════════════
function Administration({ currentUser }) {
  const [onglet, setOnglet] = useState('entreprises');

  return (
    <div>
      <div className="card" style={{ paddingBottom: 0 }}>
        <h1 className="card-title" style={{ marginBottom: '1rem' }}>Administration</h1>
        <div style={S.tabs}>
          <button style={S.tab(onglet === 'entreprises')} onClick={() => setOnglet('entreprises')}>
            🏢 Entités
          </button>
          <button style={S.tab(onglet === 'utilisateurs')} onClick={() => setOnglet('utilisateurs')}>
            👤 Utilisateurs
          </button>
          <button style={S.tab(onglet === 'documents')} onClick={() => setOnglet('documents')}>
            📄 Documents
          </button>
          <button style={S.tab(onglet === 'activites')} onClick={() => setOnglet('activites')}>
            Activités
          </button>
        </div>
      </div>

      <div style={{ marginTop: '1.5rem' }}>
        {onglet === 'entreprises' && <OngletEntreprises />}
        {onglet === 'utilisateurs' && <OngletUtilisateurs currentUser={currentUser} />}
        {onglet === 'documents' && <OngletTypesDocuments />}
        {onglet === 'activites' && <OngletTypesActivite />}
      </div>
    </div>
  );
}

export default Administration;
